import { BooleanFieldHandler } from './renderers/boolean';
import { DateFieldHandler } from './renderers/date';
import { emailValidatorCodeMapping } from './renderers/email_validator';
import { IntegerFieldHandler } from './renderers/integer';
import { FloatFieldHandler } from './renderers/float/float-field-handler';
import { PhoneFieldHandler } from './renderers/phone/phone-field-handler';
import { TagsFieldHandler } from './renderers/tags';
import { TextFieldHandler } from './renderers/text';
import { FieldValidation } from './field-validation';
import { ScoreFieldHandler } from './renderers/score/score-field-handler';
import { Inject, Injectable } from '@angular/core';
import { ValidatorFactoryService } from './validator-factory.service';
import { CustomFieldHandler } from './renderers/custom';
import { LifecycleStageSelectorHandler } from './renderers/lifecycle-stage-selector/lifecycle-stage-handler';
import { EmailFieldHandler } from './renderers/email';
import { GeopointFieldHandler } from './renderers/geopoint';
import { GooglePlaceSelectorComponent } from './renderers/google-place-selector/google-place-selector.component';
import {
  CrmDependencies,
  FormCustomInput,
  CrmInjectionToken,
  FieldSchemaInterface,
  ObjectType,
  RenderableFieldInterface,
  FieldType,
} from '../../tokens-and-interfaces';
import { StandardIds } from '../../shared-services/crm-services/field.service';

@Injectable()
export class RendererSelectorService {
  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private readonly validatorFactory: ValidatorFactoryService,
  ) {}

  defaultCustomInputs: FormCustomInput[] = [
    {
      fieldId: StandardIds.CompanyName,
      component: GooglePlaceSelectorComponent,
    },
  ];

  chooseHandler(
    objectType: ObjectType,
    fieldId: string,
    fieldSchema: FieldSchemaInterface,
    fieldData: any,
    showLabel: boolean,
    readOnly = false,
  ): RenderableFieldInterface | null {
    const label = showLabel ? fieldSchema.fieldName : '';
    const customForms: FormCustomInput[] = [];
    if (this.config[objectType.toLowerCase()]?.formCustomInputs) {
      customForms.push(...this.config[objectType.toLowerCase()].formCustomInputs);
    }
    customForms.push(...this.defaultCustomInputs);

    const customForm = customForms?.find((f) => f.fieldId === fieldId);
    if (customForm) {
      return new CustomFieldHandler(customForm, objectType, fieldId, label, fieldData, readOnly);
    }

    if (fieldId === StandardIds.CompanyLifecycleStage || fieldId === StandardIds.ContactLifecycleStage) {
      return new LifecycleStageSelectorHandler(
        objectType,
        fieldId,
        fieldSchema.fieldName,
        label,
        fieldData,
        fieldSchema.fieldDescription,
        readOnly,
      );
    }

    if (fieldId === StandardIds.CompanyPrimaryAddressLatitudeLongitude) {
      return new GeopointFieldHandler(
        objectType,
        fieldId,
        fieldSchema.fieldName,
        label,
        fieldData,
        fieldSchema.fieldDescription,
        true,
      );
    }

    if (fieldId === StandardIds.CompanyLeadScore) {
      return new ScoreFieldHandler(
        objectType,
        fieldId,
        fieldSchema.fieldName,
        label,
        fieldData,
        fieldSchema.fieldDescription,
        readOnly,
      );
    }

    switch (fieldSchema.fieldType) {
      case FieldType.FIELD_TYPE_PHONE:
        return new PhoneFieldHandler(
          objectType,
          fieldId,
          fieldSchema.fieldName,
          label,
          fieldData,
          fieldSchema.fieldDescription,
          readOnly,
        );
      case FieldType.FIELD_TYPE_STRING:
        return new TextFieldHandler(
          objectType,
          fieldId,
          fieldSchema.fieldName,
          label,
          fieldData,
          fieldSchema.fieldDescription,
          readOnly,
        );
      case FieldType.FIELD_TYPE_INTEGER:
        return new IntegerFieldHandler(
          objectType,
          fieldId,
          fieldSchema.fieldName,
          label,
          fieldData as number,
          fieldSchema.fieldDescription,
          readOnly,
        );
      case FieldType.FIELD_TYPE_FLOAT:
        return new FloatFieldHandler(
          objectType,
          fieldId,
          fieldSchema.fieldName,
          label,
          fieldData as number,
          fieldSchema.fieldDescription,
          readOnly,
        );
      case FieldType.FIELD_TYPE_DATE:
        return new DateFieldHandler(
          objectType,
          fieldId,
          fieldSchema.fieldName,
          label,
          fieldData,
          fieldSchema.fieldDescription,
          readOnly,
        );
      case FieldType.FIELD_TYPE_BOOLEAN:
        return new BooleanFieldHandler(
          objectType,
          fieldId,
          fieldSchema.fieldName,
          label,
          fieldData,
          fieldSchema.fieldDescription,
          readOnly,
        );
      case FieldType.FIELD_TYPE_EMAIL:
        return new EmailFieldHandler(
          objectType,
          fieldId,
          fieldSchema.fieldName,
          label,
          fieldData,
          fieldSchema.fieldDescription,
          readOnly,
        );
      case FieldType.FIELD_TYPE_TAG:
        return new TagsFieldHandler(
          objectType,
          fieldId,
          fieldSchema.fieldName,
          label,
          fieldData,
          fieldSchema.fieldDescription,
          readOnly,
          25,
          200,
          'MODEL_DRIVEN_FORM.ADD_TAG_PROMPT',
          'MODEL_DRIVEN_FORM.TAG_LIMIT_ERROR',
        );
      case FieldType.FIELD_TYPE_STRING_LIST:
        return new TagsFieldHandler(
          objectType,
          fieldId,
          fieldSchema.fieldName,
          label,
          fieldData,
          fieldSchema.fieldDescription,
          readOnly,
          25, // use same limit as tags
          200, // use same limit as tags
          'MODEL_DRIVEN_FORM.ADD_STRINGLIST_ITEM_PROMPT',
          '',
        );
      default:
        console.warn(`Could not choose renderer/handler for field with type ${fieldSchema.fieldType}`);
        return null;
    }
  }

  setupFieldValidators(
    field: RenderableFieldInterface,
    forExistingObjectId: string,
    fieldSchemaMap: Map<string, FieldSchemaInterface>,
  ): void {
    const schema = fieldSchemaMap.get(field.fieldId);
    const validation = new FieldValidation();
    switch (schema.fieldType) {
      case FieldType.FIELD_TYPE_EMAIL:
        validation.addValidationError(emailValidatorCodeMapping);
        validation.addValidationError(this.validatorFactory.buildUniquenessValidatorCodeMappings(field.fieldId));
        field.setValidation(validation);
        return;
      case FieldType.FIELD_TYPE_PHONE:
        validation.addValidationError(this.validatorFactory.buildUniquenessValidatorCodeMappings(field.fieldId));
        field.setValidation(validation);
        return;
      default:
        return;
    }
  }
}
